import { Fragment, type ReactNode } from 'react';

/*
 * Markdown do relatório do eval renderizado como elementos React, sem HTML cru: título, parágrafo,
 * lista, tabela, negrito e código. Qualquer outra marcação sai como texto.
 */

function inline(text: string, key: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    out.push(
      tok.startsWith('**') ? (
        <strong key={`${key}-${i++}`}>{tok.slice(2, -2)}</strong>
      ) : (
        <code key={`${key}-${i++}`} className="rounded-[var(--lr-radius-sm)] bg-sunken px-1 font-mono text-[length:var(--lr-text-sm)]">
          {tok.slice(1, -1)}
        </code>
      ),
    );
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const cells = (line: string) =>
  line
    .trim()
    .replace(/^\||\|$/g, '')
    .split(/(?<!\\)\|/)
    .map((c) => c.trim().replace(/\\\|/g, '|'));

export function Markdown({ source }: { source: string }) {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let i = 0;
  let k = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (!line.trim()) {
      i++;
      continue;
    }
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    if (h) {
      const level = h[1]!.length;
      const cls = level === 1 ? 'text-[length:var(--lr-text-lg)] font-semibold' : 'text-[length:var(--lr-text-md)] font-semibold';
      blocks.push(
        level === 1 ? (
          <h3 key={k++} className={`mt-4 ${cls}`}>
            {inline(h[2]!, `h${k}`)}
          </h3>
        ) : (
          <h4 key={k++} className={`mt-4 ${cls}`}>
            {inline(h[2]!, `h${k}`)}
          </h4>
        ),
      );
      i++;
      continue;
    }
    if (line.trim().startsWith('|')) {
      const rows: string[] = [];
      while (i < lines.length && lines[i]!.trim().startsWith('|')) rows.push(lines[i++]!);
      const [head, , ...body] = rows;
      blocks.push(
        <div key={k++} className="my-3 overflow-x-auto rounded-[var(--card-radius)] border border-border">
          <table className="w-full border-collapse text-[length:var(--lr-text-sm)]">
            <thead className="bg-sunken">
              <tr>
                {cells(head ?? '').map((c, ci) => (
                  <th key={ci} scope="col" className="px-3 py-2 text-left font-semibold text-fg-secondary">
                    {inline(c, `th${k}${ci}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {body.map((r, ri) => (
                <tr key={ri} className="border-t border-border">
                  {cells(r).map((c, ci) => (
                    <td key={ci} className="px-3 py-2">
                      {inline(c, `td${k}${ri}${ci}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }
    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i]!)) items.push(lines[i++]!.replace(/^\s*[-*]\s+/, ''));
      blocks.push(
        <ul key={k++} className="my-2 list-disc pl-5">
          {items.map((it, ii) => (
            <li key={ii}>{inline(it, `li${k}${ii}`)}</li>
          ))}
        </ul>,
      );
      continue;
    }
    const para: string[] = [];
    while (i < lines.length && lines[i]!.trim() && !/^(#{1,4}\s|\||\s*[-*]\s)/.test(lines[i]!)) para.push(lines[i++]!);
    blocks.push(
      <p key={k++} className="my-2">
        {para.map((p, pi) => (
          <Fragment key={pi}>
            {pi > 0 ? ' ' : null}
            {inline(p, `p${k}${pi}`)}
          </Fragment>
        ))}
      </p>,
    );
  }
  return <div className="text-fg">{blocks}</div>;
}
